import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    assessments: [], // Make sure it's an empty array or appropriate default state
    filteredAssessments: [],
    filters: {
      dateRange: { start: null, end: null },
      subject: null,
      chapter: null,
      topic: null,
    },
    loading: false,
    error: null,
    successMessage: null,
  };

const assessmentViewSlice = createSlice({
  name: 'assessments',
  initialState,
  reducers: {
    getAssessmentStart: (state) => {
      state.loading = true;
      state.error = null;
      state.successMessage = null;
    },
    getAssessmentSuccess: (state, action) => {
      state.loading = false;
      state.assessments = action.payload; // Store full data
      state.filteredAssessments = action.payload; // Initialize filtered data with full data
      state.successMessage = 'Assessments received successfully!';
      state.error = null;
    },
    getAssessmentFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.successMessage = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }; // Update filters in state
      if (state.assessments.length > 0) {
        state.filteredAssessments = applyFilters(state.assessments, state.filters); // Apply the filters to the full data
      }  
    },
    resetFilters: (state) => {
      state.filters = { dateRange: null, subject: null, chapter: null, topic: null }; // Reset filters
      state.filteredAssessments = state.assessments; // Reset filtered data to full data
    },
    // Optimistically drop retired assessments from the view after a successful
    // retire call. Payload: array of assessmentId strings.
    removeAssessments: (state, action) => {
      const retiredIds = new Set((action.payload || []).map(String));
      const keep = (a) => !retiredIds.has(String(a.assessmentId));
      state.assessments = state.assessments.filter(keep);
      state.filteredAssessments = state.filteredAssessments.filter(keep);
    },
    // Show a renamed paper's new name straight away, on both screens that read
    // this slice. Both lists are updated: the table renders filteredAssessments,
    // but a filter change rebuilds it from assessments, which would otherwise
    // bring the old name back. Payload: { assessmentId, assessmentName }.
    renameAssessmentInList: (state, action) => {
      const { assessmentId, assessmentName } = action.payload || {};
      if (!assessmentId || !assessmentName) return;
      const rename = (a) => {
        if (String(a.assessmentId) === String(assessmentId)) a.assessmentName = assessmentName;
      };
      state.assessments.forEach(rename);
      state.filteredAssessments.forEach(rename);
    },
    // Marked answer sheet on/off (A11) changed on a row — show it straight away,
    // in both lists (same reason as renameAssessmentInList). Payload: { assessmentId, enabled }.
    setMarkedSheetInList: (state, action) => {
      const { assessmentId, enabled } = action.payload || {};
      if (!assessmentId || typeof enabled !== 'boolean') return;
      const set = (a) => { if (String(a.assessmentId) === String(assessmentId)) a.markedSheetEnabled = enabled; };
      state.assessments.forEach(set);
      state.filteredAssessments.forEach(set);
    },
  },
});

// Function to apply the filters locally
const applyFilters = (data, filters) => {
  return data.filter((assessment) => {
    let isValid = true;

    // Date Range Filter
    if (filters.dateRange?.start && filters.dateRange?.end) {
      const assessmentDate = new Date(assessment.createdAt); // Assuming `createdAt` is the creation date field
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);
      isValid = isValid && assessmentDate >= startDate && assessmentDate <= endDate;
    }

    // Subject Filter
    if (filters.subject) {
      isValid = isValid && assessment.subject === filters.subject;
    }

    // Chapter Filter
    if (filters.chapter) {
      isValid = isValid && assessment.chapter === filters.chapter;
    }

    // Topic Filter
    if (filters.topic) {
      isValid = isValid && assessment.topic === filters.topic;
    }

    return isValid;
  });
};


export const {
  getAssessmentStart,
  getAssessmentSuccess,
  getAssessmentFailure,
  setFilters,
  resetFilters,
  removeAssessments,
  renameAssessmentInList,
  setMarkedSheetInList,
} = assessmentViewSlice.actions;

// Memoized selectors to avoid unnecessary re-renders
// Note: The slice is registered as 'viewAssessment' in the store, not 'assessments'
export const selectFilteredAssessments = (state) => state.viewAssessment?.filteredAssessments ?? [];
export const selectAssessmentsLoading = (state) => state.viewAssessment?.loading ?? false;
export const selectAssessmentsError = (state) => state.viewAssessment?.error ?? null;

export default assessmentViewSlice.reducer;